import {
  fetchDeployedVersion,
  getRunningVersion,
  hasReloadedFor,
  isNewerDeployment,
} from '@/lib/frontend-version';

/** Detect a failed lazy chunk or a webpack module factory that was not installed. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as Error;
  const message = err.message ?? '';
  const name = err.name ?? '';

  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes("Cannot read properties of undefined (reading 'call')") ||
    message.includes("Cannot read property 'call' of undefined") ||
    message.includes("reading 'call'") ||
    message.includes('reading "call"') ||
    message.includes("'call' of undefined") ||
    message.includes('"call" of undefined')
  );
}

export function chunkIdFromError(error: unknown): string | null {
  const message = error instanceof Error ? error.message : '';
  const match = message.match(/Loading chunk ([^\s]+)/) || message.match(/chunk (\d+)/i);
  return match?.[1] ?? null;
}

export type RouteFailureKind = 'checking' | 'update' | 'stale-chunk' | 'route';

export type RouteFailure = {
  kind: RouteFailureKind;
  deployed: string | null;
  running: string;
  chunkId: string | null;
  shouldReload: boolean;
};

/**
 * An update prompt is only for a deployed version.json id that differs from
 * the running bundle. A missing factory on the same version is a stale chunk,
 * and it may reload once for that build.
 */
export async function classifyRouteFailure(
  error: unknown,
  running: string = getRunningVersion(),
): Promise<RouteFailure> {
  const deployed = await fetchDeployedVersion();
  const chunkId = chunkIdFromError(error);
  if (isNewerDeployment(running, deployed)) {
    const versionKey = `version:${deployed}`;
    return {
      kind: 'update',
      deployed,
      running,
      chunkId,
      shouldReload: !hasReloadedFor(versionKey),
    };
  }
  if (isChunkLoadError(error)) {
    const versionKey = `chunk:${running}`;
    return {
      kind: 'stale-chunk',
      deployed,
      running,
      chunkId,
      shouldReload: !hasReloadedFor(versionKey),
    };
  }
  return {
    kind: 'route',
    deployed,
    running,
    chunkId,
    shouldReload: false,
  };
}
